/**
 * MODULE: _shared/lib/frontend/api/crmAccountProductsApi.ts
 * PURPOSE: HTTP client for CRM account product entitlements (/api/v1/crm/account-products)
 * PUBLIC_API: crmListAccountProducts, crmCreateAccountProduct, crmPatchAccountProduct, crmDeleteAccountProduct
 * SIDE_EFFECTS: fetch to API gateway
 * AGENT_SAFETY: RESTRICTED
 */

import { API_CONFIG, getAuthHeaders } from './config';

const BASE = `${API_CONFIG.baseURL}/api/v1/crm/account-products`;

export type CrmProductAdoptionStatus =
  | 'evaluation'
  | 'pilot'
  | 'active'
  | 'churned'
  | 'not_a_fit';

export type CrmAccountProduct = {
  id: string;
  accountId: string;
  productId: string;
  productName: string;
  productCategory?: string | null;
  productTagline?: string | null;
  status: CrmProductAdoptionStatus;
  seats?: number | null;
  mrr?: number | null;
  notes?: string | null;
  adoptedAtISO?: string | null;
  createdAtISO?: string;
  updatedAtISO?: string;
};

export type CrmAccountProductListResponse = { items: CrmAccountProduct[]; total: number };

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function crmListAccountProducts(params: {
  accountId: string;
  limit?: number;
  offset?: number;
}): Promise<CrmAccountProductListResponse> {
  const q = new URLSearchParams({ accountId: params.accountId });
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const res = await fetch(`${BASE}?${q}`, { headers: getAuthHeaders() });
  return parseJson<CrmAccountProductListResponse>(res);
}

export async function crmCreateAccountProduct(body: {
  accountId: string;
  productId: string;
  status?: CrmProductAdoptionStatus;
  seats?: number;
  mrr?: number;
  notes?: string;
  adoptedAtISO?: string;
}): Promise<CrmAccountProduct> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<CrmAccountProduct>(res);
}

export async function crmPatchAccountProduct(
  id: string,
  patch: Partial<
    Pick<CrmAccountProduct, 'status' | 'seats' | 'mrr' | 'notes' | 'adoptedAtISO'>
  >
): Promise<CrmAccountProduct> {
  const res = await fetch(`${BASE}/${id}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return parseJson<CrmAccountProduct>(res);
}

export async function crmDeleteAccountProduct(id: string): Promise<void> {
  const res = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseJson<void>(res);
}
